<template>
  <nav
    class="navBar"
    :class="[expanded ? 'expanded' : 'collapsed', darkMode ? 'dark' : 'light']"
  >
    <div v-if="sideLinks.length > 0"
        class="sideBar"
        @click="sidebarClicked"
    >
      <div class="logoHolder">
        <img
          v-if="expanded && !darkMode"
          class="logo"
          :src="logoUrl"
          @click="logoClicked"
        />
        <img
          v-if="expanded && darkMode"
          class="logo"
          :src="darkModeLogoUrl"
          @click="logoClicked"
        />
      </div>
      <a
          v-if="showSidebarToggle"
          class="toggleDetails"
          role="button"
          @click="handleSidebarToggle"
          @keyup.enter="handleSidebarToggle"
          tabindex="0"
        >
          <i v-if="expanded" class="icon fal fa-chevron-left" />
          <i v-if="!expanded" class="icon fal fa-chevron-right" />
          <span class="sr-only">Toggle navigation</span>
      </a>
      <span 
        v-if="expanded"
        class="menuButton"
        :class="menuOpen ? 'active' : ''"
        ref="menuButton"
        @click="toggleMenu"
        @keyup.enter="toggleMenu"
        tabindex="0"
      >
        <span
          v-if="currentProduct === 'monitoring'"
          class="buttonText currentApp"
        >
          <i class="productIcon fal fa-monitor-heart-rate" />
          <span class="productName">
            <div class="topLine">Technology Insights</div>
            <div class="bottomLine">
              <i class="fal fa-arrow-right" />
              Monitoring
            </div>
          </span>
          <i :class="{'toggleIcon': true, 'fal fa-chevron-left': menuOpen, 'fal fa-chevron-right': !menuOpen}" />
        </span>
        <span
          v-if="currentProduct === 'analytics'"
          class="buttonText currentApp"
        >
          <i class="productIcon fal fa-monitor-heart-rate" />
          <span class="productName">
            <div class="topLine">Technology Insights</div>
            <div class="bottomLine">
              <i class="fal fa-arrow-right" />
              Analytics
            </div>
          </span>
          <i :class="{'toggleIcon': true, 'fal fa-chevron-left': menuOpen, 'fal fa-chevron-right': !menuOpen}" />
        </span>
        <span
          v-if="currentProduct === 'workspaces'"
          class="buttonText currentApp"
        >
          <i class="productIcon fal fa-map-marked-alt" />
          <span class="productName oneLine">
            Space Insights
          </span>
          <i :class="{'toggleIcon': true, 'fal fa-chevron-left': menuOpen, 'fal fa-chevron-right': !menuOpen}" />
        </span>
        <span
          v-if="currentProduct === 'admin'"
          class="buttonText currentApp"
        >
          <i class="productIcon fal fa-toolbox" />
          <span class="productName oneLine">Admin Portal</span>
          <i :class="{'toggleIcon': true, 'fal fa-chevron-left': menuOpen, 'fal fa-chevron-right': !menuOpen}" />
        </span>
      </span>
      <div
        v-if="menuOpen"
        ref="popup"
        :class="{ navPopup: true, slideIn: menuOpen }"
      >
        <section class="options">
          <section class="applications">
            <span class="section-title">Applications</span>
            <ul class="optionList">
              <li class="techInsights">
                <span class="techTitle">
                  <i class="fal fa-monitor-heart-rate" />
                  Technology Insights
                </span>
                <ul class="sub-apps">
                  <li
                    :class="{currentApp: 'monitoring' === currentProduct}"
                  >
                    <a
                      v-if="'monitoring' !== currentProduct"
                      class="monitoring"
                      href="/#monitoring"
                    >
                      <i class="fal fa-arrow-right" />
                      Monitoring
                    </a>
                    <span v-if="'monitoring' === currentProduct">
                      <i class="fal fa-arrow-right" />
                      Monitoring
                    </span>
                  </li>
                  <li
                    :class="{currentApp: 'analytics' === currentProduct}"
                  >
                    <a
                      v-if="'analytics' !== currentProduct"
                      class="analytics"
                      href="#historical2"
                    >
                      <i class="fal fa-arrow-right" />
                      Analytics
                    </a>
                    <span v-if="'analytics' === currentProduct">
                      <i class="fal fa-arrow-right" />
                      Analytics
                    </span>
                  </li>
                </ul>
              </li>
              <li
                v-if="enabledApps.indexOf('workspaces') > -1"
                :class="{optionLink: true, currentApp: 'workspaces' === currentProduct}"
              >
                <a
                  v-if="'workspaces' !== currentProduct"
                  class="workspaces"
                  href="/#rooms"
                >
                  <i class="fal fa-map-marked-alt" />
                  Space Insights
                </a>
                <span v-if="'workspaces' === currentProduct">
                  <i class="fal fa-map-marked-alt" />
                  Space Insights
                </span>
              </li>
            </ul>
          </section>
          <section class="account">
            <span class="section-title">Account</span>
            <ul class="optionList">
              <li
                v-if="showAdmin"
                :class="{optionLink: true, currentApp: 'admin' === currentProduct}"
              >
                <a v-if="'admin' !== currentProduct" class="adminPortalLink" href="#/admin/welcome">
                  <i class="fal fa-toolbox" />
                  Admin Portal
                </a>
                <span v-if="'admin' === currentProduct">
                  <i class="fal fa-toolbox" />
                  Admin Portal
                </span>
              </li>
              <li class="optionLink">
                <a
                  target="_blank"
                  class="supportDocs"
                  :href="supportUrl"
                >
                  <i class="fal fa-newspaper" />
                  Support Center
                </a>
              </li>
              <li class="optionLink" v-if="!darkMode">
                <a
                  class="darkMode"
                  @click="toggleDarkMode"
                >
                  <i class="fal fa-moon"/>
                  Try Dark Mode
                  <em class="beta">Beta</em>
                </a>
              </li>
              <li class="optionLink" v-if="darkMode">
                <a
                  class="darkMode"
                  @click="toggleDarkMode"
                >
                  <i class="fal fa-sun"/>
                  Light Mode
                </a>
              </li>
              <li class="optionLink">
                <a
                  class="signOut"
                  @click="signOutFunction"
                >
                  <i class="fal fa-sign-out" />
                  Sign out
                </a>
              </li>
            </ul>
          </section>
        </section>
        <footer class="userFooter">
          <div>Signed in as: {{ userName }}</div>
          <div class="eula-section">
          <a href="https://www.hp.com/us-en/privacy/privacy-central.html" target="_blank">Privacy Statement</a>,
          <a href="https://support.hp.com/us-en/document/ish_12775809-12775790-16" target="_blank">EULA</a>,
          and <a href="https://www.hp.com/content/dam/sites/worldwide/privacy/pdf/master-level/PARTNER-DPA-202109.pdf" target="_blank">DPA</a>
          </div>
        </footer>
      </div>
      <ul
        v-if="sideLinks && sideLinks.length > 0 && expanded"
        class="sideBarLinks styleScroll"
        show-arrows
        center-active
        direction="vertical"
      >
        <li
          v-for="(link, i) in sideLinks"
          :key="i"
          class="sideLink topLevel"
        >
          <a
            v-if="link.url && !link.routeName"
            :href="link.url"
            :target="link.target"
            :class="getLinkClass(link.url, currentUrl)"
          >
            <i v-if="link.icon" :class="'icon ' + link.icon" />
            <span>{{ link.title || link.name }}</span>
            <em v-if="link.badge === 'beta'" class="beta">Beta</em>
            <em v-if="link.badge === 'alpha'" class="alpha">Alpha</em>
          </a>
          <span
            v-if="!link.url && !link.routeName"
          >
            <i v-if="link.icon" :class="'icon ' + link.icon" />
            <span>{{ link.title || link.name }}</span>
            <em v-if="link.badge === 'beta'" class="beta">Beta</em>
            <em v-if="link.badge === 'alpha'" class="alpha">Alpha</em>
          </span>
          <router-link
            v-if="link.routeName"
            :to="{ name: link.routeName, params: link.params }"
            :class="getLinkClass(link.url, currentUrl)"
          >
            <i :class="link.icon" />
            {{ link.title || link.name }}
            <em v-if="link.badge === 'beta'" class="beta">Beta</em>
            <em v-if="link.badge === 'alpha'" class="alpha">Alpha</em>
          </router-link>
          <ul
            v-if="link.views && link.views.length > 0"
            class="sublevelLinks"
          >
            <li
              v-for="(view, j) in link.views"
              :key="j"
              class="sideLink subLevel"
            >
              <a
                v-if="!view.routeName"
                :href="view.url"
                :target="view.target"
                :class="getLinkClass(view.url, currentUrl)"
              >
                <i v-if="view.icon" :class="'icon ' + view.icon" />
                <span>{{ view.title || view.name }}</span>
                <em v-if="view.badge === 'beta'" class="beta">Beta</em>
                <em v-if="view.badge === 'alpha'" class="alpha">Alpha</em>
              </a>
              <router-link
                v-if="view.routeName"
                :to="{ name: view.routeName, params: view.params }"
                :class="getLinkClass(view.url, currentUrl)"
              >
                <i :class="view.icon" />
                {{ view.title || view.name }}
              </router-link>
              <ul v-if="view.tabs">
                <li
                  v-for="(tab, k) in view.tabs"
                  :key="k"
                  class="sideLink bottomLevel"
                >
                </li>
              </ul>
            </li>
          </ul>
        </li>
      </ul>
    </div>
    <slot name="body" class="mainContent" />
  </nav>
</template>

<script>
import DataProvider from '../../../app/data-framework/data-provider';
import BrandingService from '../../../services/branding.service';

  let navBar = {
    name: 'NavBar',
    data: function() {
      let self = this;
      let logoUrl = this.userInfo.logo || BrandingService.getNavLogo() || 'dist/images/WorkforceExperienceLogo_light.svg';
      let darkModeLogoUrl = BrandingService.getNavLogoDark() || 'dist/images/WorkforceExperienceLogo_dark.svg';
      return {
        currentUrl: window.location.href,
        darkMode: document.body.classList.contains('dark'),
        dataProvider: DataProvider.getInstance(),
        expanded: true,
        logoUrl,
        darkModeLogoUrl,
        menuOpen: false,
        outsideClickListener: event => {
          if (self.menuOpen) {
            let menuButtonElement = self.$refs.menuButton;
            let popupElement = self.$refs.popup;
            if (menuButtonElement && !menuButtonElement.contains(event.target) && popupElement && !popupElement.contains(event.target)) {
              self.toggleMenu();
            }
          }
        },
      };
    },
    components: {
    },
    props: {
      currentProduct: {
        type: String,
        default: 'monitoring'
      },
      enabledApps: {
        type: Array,
        default: function() {
          return [];
        }
      },
      showAdmin: {
        type: Boolean,
        default: false
      },
      sideLinks: {
        type: Array,
        default: function() {
          return [];
        }
      },
      showRightSide: {
        type: Boolean,
        default: true
      },
      showSidebarToggle: {
        type: Boolean,
        default: true
      },
      supportUrl: {
        type: String,
        default: 'https://learn.workforceexperience.hp.com'
      },
      supportContactUrl: {
        type: String,
        default: '',
      },
      userName: {
        type: String,
        default: ''
      },
      userInfo: {
        type: Object,
        default: undefined
      }
    },
    created() {
      let self = this;

      const escapeHandler = (e) => {
        if (e.key === '[' && !e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) {
          self.handleSidebarToggle();
        }
      }

      document.addEventListener('keydown', escapeHandler);

      this.$once('hook:destroyed', () => {
        document.removeEventListener('keydown', escapeHandler);
      });

    },
    mounted: function() {
      window.addEventListener('hashchange', this.setCurrentUrl);
      if (window.document.body.classList.contains('sidebar-collapse')) {
        this.expanded = false;
      }
      
      if (window.document.body.classList.contains('dark')) {
        this.darkMode = true;
      }
    },
    beforeDestroy: function() {
      window.document.removeEventListener('click', this.outsideClickListener);
      window.removeEventListener('hashchange', this.setCurrentUrl);
    },
    methods: {
      getLinkClass: function(link, currentHref) {
        if (link && currentHref.indexOf(link.slice(2)) > -1) {
          return 'active';
        }
        return '';
      },
    
      /**
       * Handles the sidebar shrink/expand toggle.
       *
       * @listens onclick, side menu button
       */
      handleSidebarToggle: function(event) {
        if (this.expanded) {
          this.expanded = false;
          document.body.classList.add('sidebar-collapse');
          this.menuOpen = false;
        } else {
          this.expanded = true;
          document.body.classList.remove('sidebar-collapse');
        }
        /**
         * Value change event for side bar width.
         *
         * @type {boolean}
         */
        this.$emit('side-bar-toggle', this.expanded);
        if (event) event.stopImmediatePropagation();
      },
      toggleDarkMode: function() {
        this.toggleMenu();
        if (this.darkMode) {
          this.setTheme('light', 'user toggle');
        } else {
          this.setTheme('dark', 'user toggle');
        }
      },
      setTheme: function(theme, source) {
        document.body.classList.remove('light');
        document.body.classList.remove('dark');
        document.body.classList.add(theme);
        this.darkMode = theme === 'dark';
        window.localStorage.setItem('theme', theme);
        this.dataProvider.logActivity2('set theme', 'navBar.vue', theme + ' set by ' + source);
      },
      sidebarClicked: function() {
        if (!this.expanded) {
          this.expanded = true;
          document.body.classList.remove('sidebar-collapse');
          this.$emit('side-bar-toggle', this.expanded);
        }
      },
      logoClicked: function(e) {
        if (e.metaKey || (e.altKey && e.shiftKey)) {
          this.$emit('secret-meta-click', e);
        }else if (e.altKey) {
          /**
           * Event for if logo is alt clicked for secret functionality.
           *
           * @type {ClickEvent}
           */
          this.$emit('secret-click', e);
        }
      },
      setCurrentUrl: function() {
        this.currentUrl = window.location.href;
      },
      signOutFunction: function(e) {
        /**
         * Event for when sign out is clicked.
         *
         * @type {ClickEvent}
         */
        this.$emit('sign-out', e);
      },
      toggleMenu: function() {
        if (!this.menuOpen) {
          // add dismiss click listener
          // using setTimeout so that the callback isn't called immediately as part of the current event loop
          setTimeout(() => window.document.addEventListener('click', this.outsideClickListener), 0);
          this.menuOpen = true;
        } else {
          // remove click listener
          window.document.removeEventListener('click', this.outsideClickListener);
          this.menuOpen = false;
        }
      }
    }
  };
  export default navBar;
</script>

<style lang="scss">
/* Vue transition styles */
.fade-enter {
  opacity: 0;
}
.fade-enter-active {
  transition: opacity 0.1s ease-in-out 0.3s;
}
.fade-leave-to {
  opacity: 0;
}
.fade-leave-active {
  transition: opacity 0.1s ease-in-out;
}

.fade-mini-enter {
  opacity: 100;
}
.fade-mini-enter-active {
  transition: opacity 0.0s ease-in-out;
}
.fade-mini-leave-to {
  opacity: 100;
}
.fade-mini-leave-active {
  transition: opacity 0.0s ease-in-out 0.4s;
}
.logoHolder {
  color: #212121;
  display: inline-block;
  width: 175px;
  margin: 23.5px 20.5px 17.5px 19.5px;
  position: relative;
  transition: all 0.27s ease-out .13s;
  height: 42px;
  overflow-x: hidden;
  &.mini {
    width: 25px;
    transition: all 0.27s ease-in;
  }
  .logo {
    height: 32px;
    width: auto;
  }
}

.dark .logoHolder {
  color: #ffffff;
}


/* navBar component styles */
.navBar {
  color: var(--text-color);
  display: flex;
  flex-direction: row;

  .toggleDetails {
    position: absolute;
    font-size: 13px;
    top: 28px;
    left: calc(var(--side-bar-width) - 24px);
    display: inline-block;
    height: 32px;
    width: 24px;
    background-color: var(--default-color);
    border-top-left-radius: var(--border-radius);
    border-bottom-left-radius: var(--border-radius);
    cursor: pointer;
    vertical-align: top;
    transition: all 0.4s ease-in-out;
    .icon {
      color: var(--toggle-color);
      margin-top: 9px;
      margin-left: 10px;
    }
  }

  .menuButton {
    display: inline-block;
    vertical-align: top;
    font-size: 18px;
    line-height: 18px;
    border: 1px solid lightgray;
    color: var(--text-color);
    width: calc(100% - 40px);
    height: 50px;
    cursor: pointer;
    position: relative;
    margin: 1px 20px 10px 20px;
    border-radius: var(--border-radius);

    &:hover {
      background-color: var(--secondary-btn-hover-bg-color);
      border: 1px solid var(--secondary-btn-hover-bg-color);
    }
    &.active {
      background-color: var(--active-background);
      border: 1px solid var(--active-background);
    }

    .buttonText {
      display: inline-block;
      padding: 9px 20px 0px 20px;
      width: calc(var(--side-bar-width) - 40px);
      text-wrap-mode: nowrap;
    }
    .productIcon {
      position: absolute;
      top: 16px;
    }
    .productName {
      display: inline-block;
      font-size: 14px;
      line-height: 14px;
      height: 28px;
      font-weight: 500;
      margin-left: 30px;
      &.oneLine {
        height: 14px;
        padding-top: 9px;
      }
      .topLine {
        margin: auto;
        line-height: 16px;
      }
      .bottomLine {
        font-weight: 400;
        font-size: 13px;
        line-height: 13px;
        i {
          font-size: 10px;
          padding-right: 2px;
        }
      }
    }
    .toggleIcon {
      float: right;
      padding-top: 8px;
    }
  }
  .navPopup {
    position: absolute;
    left: calc(var(--side-bar-width) + 4px);
    top: 80px;
    z-index: 1000;
    background-color: var(--panel-background);
    padding: 30px 0 10px 0;
    border-radius: 0 0 3px 3px;
    color: var(--text-color);
    text-decoration: none;
    box-shadow: rgba(77, 82, 229, 0.2) 0px 1px 4px 1px;
    border: 1px solid #eeeeee;
    border-radius: var(--border-radius);

    a {
      text-decoration: none;
    }

    &.slideIn {
      overflow-y: hidden;
      transition: all 1s linear;
    }
    .options {
      display: flex;
      flex-direction: row;

      .account {
        border-left: 1px solid #757575;
      }
      .applications, .account {
        padding: 5px 20px;
        width: 225px;
        .section-title {
          font-family: var(--font-family);
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 25px;
          margin-left: 10px;
          text-transform: uppercase;
          color: var(--text-color);
          letter-spacing: unset;
        }
        .optionList {
          list-style: none;
          padding: 0;
          padding-top: 10px;
          i {
            margin: 10px;
          }
          .techInsights {
            line-height: 13px;
            font-size: 13px;
            width: 100%;
            margin-bottom: 16px;
            .techTitle {
              display: block;
              height: 20px;
              font-weight: 500;
            }
            .sub-apps {
              list-style: none;
              padding: 0;
              padding-left: 30px;

              li {
               color: var(--text-color);
                height: 22px;

                a {
                  i {
                    font-size: 12px;
                  }
                 color: var(--text-color);
                  display: block;
                }
                a:hover, a:active {
                  color: var(--hover-color);
                }
                &.currentApp span {
                  color: var(--hover-color);
                  cursor: default;
                }
              }
            }
          }
          .optionLink {
            a, span {
              display: inline-block;
              line-height: 15px;
              font-size: 13px;
              width: 100%;
              height: 40px;
              color: var(--text-color);
              font-weight: 500;
              cursor: pointer;
              border-radius: var(--border-radius);
              &:hover, &:active {
                color: var(--hover-color);
                .learnAbout {
                  color: var(--hover-color);
                }
              }
              &.learnLink {
                i {
                  position: relative;
                  top: 7px;
                }
                .learnAbout {
                  width: 115px;
                  display: inline-block;
                  vertical-align: middle;
                  padding-top: 8px;
                }
              }
              &.signOut {
                cursor: pointer;
              }
            }
            &.currentApp a, &.currentApp span {
              color: var(--hover-color);
              cursor: default;
            }
          }
        }
      }
    }
    .userFooter {
      width: 100%;
      text-align: center;
      font-size: 11px;
      margin-top: 15px;
      line-height: 12px;

      .eula-section {
        margin-top: 12px;
      }
    }
  }

  .mainContent {
    flex-grow: 1;
  }

  .sideBar {
    height: 100vh;
    transition: width .4s ease-in-out;
    width: var(--side-bar-width);
    line-height: 0;
    overflow-x: clip;
    background-color: var(--panel-background);
    scrollbar-color: var(--scrollbar-thumb-color) var(--panel-background);

    ul {
      list-style-type: none;
      padding: 0;
    }

    .v-slide-group__content {
      flex-direction: column;
    }
    .sideBarLinks {
      padding: 10px 20px;
      height: calc(100vh - 145px);
      overflow: hidden;
      overflow-y: auto;
      width: var(--side-bar-width);
      .sideLink {
        display: block;
        position: relative;

        .active .icon {
          font-weight: 800;
        }
        .icon {
          margin-right: 6px;
          font-size: 15px;
          position: relative;
        }

       > a, > span {
          align-items: center;;
          display: flex;          
          text-decoration: none;
          color: var(--text-color);
          width: 100%;
          height: 100%;
          cursor: pointer;
          padding: 6px 20px;
          height: 32px;
          text-align: left;
          font-size: 16px;
          font-weight: 400;
          line-height: normal;
          letter-spacing: unset;
          text-transform: capitalize;
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
          border-top-right-radius: var(--border-radius);
          border-bottom-right-radius: var(--border-radius);

          &:hover, &:focus {
            color: var(--hover-color);
            background-color: var(--secondary-btn-hover-bg-color);
          }
          &.active {
            color: var(--active-color);
            background-color: var(--active-background);
          }
        }
        &.topLevel > a {
          font-size: 16px;
          margin-bottom: 2px;
          padding: 10px 20px;
          border-radius: var(--border-radius);
          height: 40px;

          &:hover, &:focus {
            color: var(--hover-color);
            background-color: var(--secondary-btn-hover-bg-color);
          }
          &.active {
            color: var(--active-color);
            background-color: var(--active-background);
          }
        }
        &.topLevel > span {
          font-size: 16px;
          margin-bottom: 2px;
          cursor: unset;
          padding: 10px 20px;
          border-radius: var(--border-radius);
          height: 40px;

          &:hover, &:focus {
            color: var(--hover-color);
          }
          &.active {
            color: var(--active-color);
          }
          &.active::before {
            background-color: var(--active-color);
          }
          &:hover::before, &:focus::before {
            background-color: var(--hover-color);
          }
          &::before {
            opacity: 1;
            border: 2px solid var(--panel-background);
            border-radius: 4px;
            box-sizing: content-box;
            content: " ";
            height: 8px;
            left: -2px;
            position: absolute;
            transition: opacity 100ms linear, background-color 100ms linear, color 100ms linear;
            width: 2px;
            align-items: center;
          }

        }
        &.subLevel > a {
          border-left: 2px solid var(--tabs-border);
          &.active {
            border-color: var(--active-color);
          }
        }
        &.subLevel > a:hover, &.subLevel > a:focus {
          border-left: 2px solid var(--hover-color);
        }

        .sublevelLinks {
          margin-bottom: 6px;
          .subLevel {
            margin-left: 25px;
          }
        }
        .bottomLevel {
          margin-left: 6px;
        }

        &.topLevel > a.active::before {
          background-color: var(--active-color);
        }
        &.topLevel > a:hover::before {
          background-color: var(--hover-color);
        }
        &.topLevel > a::before {
          opacity: 1;
          border: 2px solid var(--panel-background);
          border-radius: 4px;
          box-sizing: content-box;
          content: " ";
          height: 8px;
          left: -2px;
          position: absolute;
          transition: opacity 100ms linear, background-color 100ms linear, color 100ms linear;
          width: 2px;
          align-items: center;
        }
        
      }
    }
  }
    
  &.collapsed .sideBar {
    width: 10px;
    cursor: pointer;
    padding: 0;
    overflow-x: hidden;
    &:hover, &:focus {
      border-right: 2px solid var(--default-color);
    }
    .toggleDetails {
      left: 10px;
      border-radius: 0px;
      border-top-right-radius: var(--border-radius);
      border-bottom-right-radius: var(--border-radius);
    }
  }
}

</style>
